// X21-RESA-13: the product statuses of the public catalogue. One list per workspace, kept as the workspace setting
// `productStatuses` (read with settingOf on the server, useSetting in the editor). A product item names one key from it.
import { z } from "zod";

export const PRODUCT_BADGE = z.enum(["neutral", "info", "success", "warning", "danger"]);
export type ProductBadge = z.infer<typeof PRODUCT_BADGE>;

export const PRODUCT_STATUS = z.object({
  /** A slug, unique in the list. Products store this key, so it is set once when the status is added. */
  key: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, digits and hyphens.").max(40),
  label: z.string().trim().min(1).max(40),
  /** Shown to staff only. */
  internalDescription: z.string().max(300).default(""),
  /** The button a product with this status shows on the site. Blank shows no status button. */
  buttonText: z.string().max(40).default(""),
  badge: PRODUCT_BADGE,
  /** Customers can use or buy the product now. */
  available: z.boolean(),
  /** Staff can choose this status for a product. */
  selectable: z.boolean(),
  order: z.number().int().min(0).max(999),
});
export type ProductStatus = z.infer<typeof PRODUCT_STATUS>;

const uniqueKeys = (list: { key: string }[]) => new Set(list.map((s) => s.key)).size === list.length;

export const PRODUCT_STATUSES = z.array(PRODUCT_STATUS).max(30)
  .refine(uniqueKeys, "Two statuses share a key.")
  .refine((list) => list.some((s) => s.selectable), "Keep at least one status that products can use.");

export const DEFAULT_PRODUCT_STATUSES: ProductStatus[] = [
  { key: "live", label: "Live", internalDescription: "Sold and supported now.", buttonText: "Get started", badge: "success", available: true, selectable: true, order: 0 },
  { key: "beta", label: "Beta", internalDescription: "Open to early users. Feedback is welcome.", buttonText: "Try the beta", badge: "info", available: true, selectable: true, order: 1 },
  { key: "coming-soon", label: "Coming soon", internalDescription: "Announced, not open yet.", buttonText: "Join the waitlist", badge: "neutral", available: false, selectable: true, order: 2 },
  { key: "in-development", label: "In development", internalDescription: "Being built. Not on sale.", buttonText: "", badge: "neutral", available: false, selectable: false, order: 3 },
  { key: "sunset", label: "Sunset", internalDescription: "Still supported while it is retired. Do not sell.", buttonText: "Ask about alternatives", badge: "warning", available: true, selectable: false, order: 4 },
  { key: "archived", label: "Archived", internalDescription: "Retired and hidden from new buyers.", buttonText: "", badge: "neutral", available: false, selectable: false, order: 5 },
];

/** The status a product item has when it names none (an entry saved before X21-RESA-13 has no status). */
export const DEFAULT_PRODUCT_STATUS_KEY = "live";

/** The status a product item shows: its own key, or the default key when it names none. Undefined when the key is not in the list. */
export function productStatusOf(statuses: readonly ProductStatus[], key: string | undefined): ProductStatus | undefined {
  const wanted = key && key.trim() ? key.trim() : DEFAULT_PRODUCT_STATUS_KEY;
  return statuses.find((s) => s.key === wanted);
}

/** A walkthrough link: blank (the button is hidden), a path on this site, or an https address. */
export const isWalkthroughHref = (u: string) => u === "" || /^\/(?!\/)/.test(u) || /^https:\/\/\S+$/i.test(u);

/** The public path of the sales and contact page that every product's calls to action link to. */
export const PRODUCT_CONTACT_PATH = "/contact";
